import React, {CSSProperties, ReactNode, useMemo} from 'react';
import { defined } from '@unifocus/js-utils';
import { BorderPanel } from '../../components/layout/borderpanel';
import { VerticalPanel } from '../../components/layout/verticalpanel';
import {Heading} from '../../components/display/heading';

export interface SectionPanelTitleBarProps {
   /**
    * The title of the section.
    */
   title?: string;

   /**
    * A section wide action. This is placed on the same row as title, and is right justified.
    */
   action?: ReactNode;
}

/**
 * {@docCategory SectionPanel}
 */
export interface SectionPanelHeaderProps {
   /**
    * The footer content.
    */
   content?: ReactNode;
}

/**
 * {@docCategory SectionPanel}
 */
export interface SectionPanelFooterProps {
   /**
    * The footer content.
    */
   content?: ReactNode;

   /**
    * Determines whether the footer section has a gray background.
    *
    * @default false
    */
   opaque?: boolean;
}

/**
 * {@docCategory SectionPanel}
 */
export interface SectionPanelProps {
   titleBar?: SectionPanelTitleBarProps;

   /**
    * An optional top header
    */
   header?: SectionPanelHeaderProps | ReactNode;

   /**
    * An optional bottom footer
    */
   footer?: SectionPanelFooterProps | ReactNode;

   /**
    * Determines whether or not the component has a transparent background.
    *
    * @default false
    */
   transparent?: boolean;

   /**
    * Determines if a loading mask is displayed over the component.
    *
    * @default false
    */
   loading?: boolean;

   /**
    * Determines if the component has a border around it.
    *
    * @default false
    */
   border?: boolean;

   /**
    * If border is set, adds a drop shadow for more contrast in certain use cases
    *
    * @default false
    */
   dropShadow?: boolean;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Determines if the panel will fill its parent's width and height.
    *
    * @defaultvalue false
    */
   fill?: boolean;

   /**
    * Determines if the panel will fill its parent's width.
    *
    * @defaultvalue false
    */
   horizontalFill?: boolean;

   /**
    * Determines if the panel will fill its parent's height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * Set the width of the component. By default, the panel will "flex" to take up all available
    * space in it's parent container.
    * Normally this should not be used.
    *
    * @defaultvalue '100%' if horizontalFill is true; otherwise 'max-content'
    */
   width?: string | number;

   /**
    * Set the height of the component. By default, the panel will "flex" to take up all available
    * space in it's parent container.
    * Normally this should not be used.
    *
    * @defaultvalue '100%' if verticalFill is true; otherwise 'max-content'
    */
   height?: string | number;

   /**
    * The minimum width of the panel.
    *
    * @defaultvalue 0
    */
   minWidth?: string | number;

   /**
    * The minimum height of the panel.
    *
    * @defaultvalue 0
    */
   minHeight?: string | number;

   /**
    * The maximum width of the panel.
    *
    * @defaultvalue undefined
    */
   maxWidth?: string | number;

   /**
    * The maximum height of the panel.
    *
    * @defaultvalue undefined
    */
   maxHeight?: string | number;

   /**
    * Sets a className on the outer panel.
    * Normally this should not be used.
    */
   className?: string;

   /**
    * Sets styles on the outer panel.
    * Normally this should not be used.
    */
   style?: CSSProperties;

   /**
    * Sets the id of the element in the DOM.
    */
   id?: string;
}

export const SectionPanel = (props: React.PropsWithChildren<SectionPanelProps>) => {
   const horizontalFill = defined(props.horizontalFill) ? props.horizontalFill : defined(props.fill) ? props.fill : true;
   const verticalFill = defined(props.verticalFill) ? props.verticalFill : defined(props.fill) ? props.fill : true;

   const topPanel = useMemo(() => (<SectionPanelTopPanel {...props}/>), [props]);
   const bottomPanel = useMemo(() => (<SectionPanelBottomPanel {...props}/>),[props]);

   return (
      <BorderPanel
         className={`uf-section-panel ${props.className || ''} ${props.transparent ? 'uf-section-panel-transparent' : ''} ${props.border ? 'uf-section-panel-border' : ''} ${props.border && props.dropShadow ? 'uf-section-panel-border-drop-shadow' : ''}`}
         style={props.style}
         id={props.id}
         horizontalFill={horizontalFill}
         verticalFill={verticalFill}
         width={props.width}
         height={props.height}
         minWidth={props.minWidth}
         minHeight={props.minHeight}
         maxWidth={props.maxWidth}
         maxHeight={props.maxHeight}
         bottom={bottomPanel}
      >
         <BorderPanel
            top={topPanel}
         >
            <SectionPanelContentPanel {...props}/>
         </BorderPanel>
      </BorderPanel>
   );
};

const SectionPanelContentPanel = (props: React.PropsWithChildren<SectionPanelProps>) => {
   if (props.scroll === true) {
      return (
         <div className='uf-section-panel-scrolling-container'>
            <div className='uf-section-panel-content-container'>
               {props.children}
            </div>
         </div>
      );
   }

   return (
      <div className='uf-section-panel-non-scrolling-container'>
         <div className='uf-section-panel-content-container'>
            {props.children}
         </div>
      </div>
   );
};

const SectionPanelTopPanel = (props: SectionPanelProps) => {

   const headerContent = useMemo(() => (props.header ? (React.isValidElement(props.header) ?
                                                        props.header :
                                                        (props.header as SectionPanelHeaderProps).content)
                                                     : undefined),
                                 [props.header]);

   if ((props.titleBar && props.titleBar.title) || headerContent) {
      return (
         <>
            <SectionPanelTitleBar {...props}/>
            <SectionPanelHeaderPanel content={headerContent}/>
         </>
      );
   }

   return null;
};

const SectionPanelTitleBar = (props: SectionPanelProps) => {
   if (props.titleBar && props.titleBar.title) {
      return (
         <>
            <div className='uf-section-panel-titlebar'>
               {props.titleBar.title &&
                <Heading level={4} ellipsis={{ tooltip: true }} className='uf-section-panel-title-text'>
                  {props.titleBar.title}
                </Heading>
               }
               {props.titleBar.action &&
                <div className='uf-section-panel-action-container'>
                   {props.titleBar.action}
                </div>}
            </div>
            <div
               className={props.border ? 'uf-section-panel-title-divider-border' : 'uf-section-panel-title-divider'}
            />
         </>
      );
   }

   return null;
};

const SectionPanelHeaderPanel = (props: SectionPanelHeaderProps) => {
   if (props.content) {
      return (
         <VerticalPanel gap={'small'} className={'uf-section-panel-header-panel'}>
            {props.content}
         </VerticalPanel>
      );
   }

   return null;
};

const SectionPanelBottomPanel = (props: SectionPanelProps) => {

   const footerContent = useMemo(() => (props.footer ? (React.isValidElement(props.footer) ?
                                                        props.footer :
                                                        (props.footer as SectionPanelFooterProps).content)
                                                     : undefined),
                                 [props.footer]);
   if (props.footer) {
      const footerIsOpaque = !React.isValidElement(props.footer) ? (props.footer as SectionPanelFooterProps).opaque : false;
      return (
         <footer
            className={`${(props.transparent || !footerIsOpaque) ? 'uf-section-panel-footer' : 'uf-section-panel-footer-opaque'}`}
         >
            <div className='uf-section-panel-footer-inner'>
               {footerContent}
            </div>
         </footer>
      );
   }

   return null;
};
